import { useState } from 'react';
import { 
  FaSearch, 
  FaFilter, 
  FaClipboardList,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaRupeeSign,
  FaChalkboardTeacher,
  FaUsers
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import { formatDateLocale } from '../../utils/dateFormatter';

function AllPrograms() {
  const [trainerFilter, setTrainerFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (trainerFilter) params.append('trainerId', trainerFilter);
    if (isActiveFilter !== '') params.append('isActive', isActiveFilter);
    params.append('page', page.toString());
    params.append('limit', '12');
    return `/api/admin/programs?${params.toString()}`;
  };

  const { data, error } = useSWR(buildQuery(), Fetcher);
  const programs = data?.data?.programs || [];
  const pagination = data?.data?.pagination || { total: 0, pages: 1 };

  const filteredPrograms = programs.filter(program => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        program.name.toLowerCase().includes(query) ||
        program.description?.toLowerCase().includes(query) ||
        (program.trainerId && typeof program.trainerId === 'object' && program.trainerId.name?.toLowerCase().includes(query))
      );
    }
    return true;
  });

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Programs</h1>
          <p className="text-gray-600">View and manage all training programs</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <select
              value={isActiveFilter}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button
              onClick={() => {
                setTrainerFilter('');
                setIsActiveFilter('');
                setSearchQuery('');
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Programs Grid */}
        {error ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-red-600">Failed to load programs</p>
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaClipboardList className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Programs Found</h2>
            <p className="text-gray-600">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {filteredPrograms.map((program) => (
                <div key={program._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{program.name}</h3>
                      {program.trainerId && typeof program.trainerId === 'object' && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <FaUser className="mr-2 text-gray-400" />
                          <span className="capitalize">{program.trainerId.name}</span>
                          {program.trainerId.service && (
                            <span className="ml-2 text-gray-500">({program.trainerId.service})</span>
                          )}
                        </div>
                      )}
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        program.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {program.isActive ? (
                        <FaCheckCircle className="inline mr-1" />
                      ) : (
                        <FaTimesCircle className="inline mr-1" />
                      )}
                      {program.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {program.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{program.description}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium text-gray-900">
                        {program.duration} {program.durationUnit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Classes:</span>
                      <span className="font-medium text-gray-900">
                        {program.classesPerDuration} per {program.durationUnit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Price:</span>
                      <span className="font-bold text-green-600 flex items-center">
                        <FaRupeeSign className="mr-1" />
                        {program.price || program.currentPrice || program.basePrice || 0}
                      </span>
                    </div>
                    {program.effectiveDate && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Effective Date:</span>
                        <span className="font-medium text-gray-900">
                          {formatDateLocale(program.effectiveDate)}
                        </span>
                      </div>
                    )}
                    {program.classCount !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 flex items-center">
                          <FaChalkboardTeacher className="mr-1 text-gray-400" />
                          Linked Classes:
                        </span>
                        <span className="font-medium text-gray-900">{program.classCount}</span>
                      </div>
                    )}
                    {program.studentCount !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 flex items-center">
                          <FaUsers className="mr-1 text-gray-400" />
                          Enrolled Students:
                        </span>
                        <span className="font-medium text-gray-900">{program.studentCount}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AllPrograms;

